import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFamilyScope } from '@/contexts/FamilyScopeContext';
import { useFamilyCapabilities } from '@/hooks/useFamilyCapabilities';
import { isRouteAllowedForPath } from '@/lib/familyNavigation';

/**
 * While a family view is selected, someone whose role doesn't cover the screen
 * they landed on is taken to the closest screen their role does cover, instead
 * of being shown a dead-end panel. The personal account is never redirected.
 *
 * Called from the app shell and from the dashboard, which owns its own chrome.
 */
export function useFamilyRouteFallback() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { scope } = useFamilyScope();
  const caps = useFamilyCapabilities();
  const inFamilyScope = scope.type === 'family';

  useEffect(() => {
    if (!inFamilyScope || caps.loading) return;
    if (isRouteAllowedForPath(pathname, caps, true)) return;
    const fallback = caps.canScanReceipt
      ? '/scanner'
      : caps.canViewExpenses
        ? '/dashboard'
        : '/family';
    if (fallback !== pathname) navigate(fallback, { replace: true });
  }, [inFamilyScope, caps, pathname, navigate]);
}
